import Button from '@components/v2/ui/Button/Button';
import Icon from '@components/v2/ui/Icon/Icon';
import IconButton from '@components/v2/ui/IconButton/IconButton';
import Menu from '@components/v2/ui/Menu/Menu';
import MenuItem from '@components/v2/ui/Menu/MenuItem';
import Typography from '@components/v2/ui/Typography/Typography';
import { Stack } from '@mui/material';
import { Ellipsis } from 'lucide-react';
import LangEnum from '../../../../../../lang/LangEnum';
import { useTranslation } from '../../../../../translation/TranslationContext';
import { CatalogHeaderAction } from '../../catalogHeaderActions.types';
import useCatalogMoreActionsButton from './hooks/useCatalogMoreActionsButton';

interface CatalogMoreActionsButtonProps {
  catalogId: string;
  isCatalogActive?: boolean;
  isReadOnly: boolean;
  isCompact?: boolean;
  groupedActions?: CatalogHeaderAction[];
}

const CatalogMoreActionsButton = ({
  catalogId,
  isCatalogActive,
  isReadOnly,
  isCompact,
  groupedActions = [],
}: CatalogMoreActionsButtonProps) => {
  const { translate } = useTranslation();
  const {
    anchorEl,
    isOpen,
    isPending,
    statusActionLabel,
    statusActionIcon,
    openMenu,
    closeMenu,
    toggleStatus,
  } = useCatalogMoreActionsButton({ isCatalogActive, catalogId });

  const hasStatusAction = !isReadOnly && isCatalogActive !== undefined;

  if (!hasStatusAction && groupedActions.length === 0) {
    return null;
  }

  const handleGroupedAction = (action: CatalogHeaderAction) => () => {
    closeMenu();
    action.onClick();
  };

  const renderTrigger = () => {
    if (isCompact) {
      return (
        <IconButton
          icon={Ellipsis}
          size="medium"
          ariaLabel={translate(LangEnum.GLOBAL_MORE_ACTIONS)}
          ariaHasPopup
          ariaExpanded={isOpen}
          onClick={openMenu}
        />
      );
    }

    return (
      <Button
        variant="text"
        color="secondary"
        size="medium"
        startIcon={<Icon icon={Ellipsis} size="xs" />}
        aria-haspopup="true"
        aria-expanded={isOpen}
        onClick={openMenu}
      >
        {translate(LangEnum.GLOBAL_MORE_ACTIONS)}
      </Button>
    );
  };

  return (
    <>
      {renderTrigger()}

      <Menu
        anchorEl={anchorEl}
        open={isOpen}
        onClose={closeMenu}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        {groupedActions.map((action) => (
          <MenuItem key={action.label} onClick={handleGroupedAction(action)}>
            <Stack direction="row" sx={{ alignItems: 'center', gap: 1 }}>
              <Icon icon={action.icon} size="xs" />
              <Typography variant="body2">{translate(action.label)}</Typography>
            </Stack>
          </MenuItem>
        ))}

        {hasStatusAction && (
          <MenuItem disabled={isPending} onClick={toggleStatus}>
            <Stack direction="row" sx={{ alignItems: 'center', gap: 1 }}>
              <Icon icon={statusActionIcon} size="xs" />
              <Typography variant="body2">{translate(statusActionLabel)}</Typography>
            </Stack>
          </MenuItem>
        )}
      </Menu>
    </>
  );
};

export default CatalogMoreActionsButton;
